import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BsRobot, BsTwitterX, BsInstagram, BsEnvelope } from 'react-icons/bs';
import { FaFacebookF, FaDiscord } from 'react-icons/fa';
import { trackEvent } from '../../utils/analytics';

const footerLinks = {
  Product: [
    { label: 'AI Tutor', href: '/mode?category=school' },
    { label: 'Practice', href: '/mode?category=school' },
    { label: 'Generate Paper', href: '/mode?category=school' },
    { label: 'School', href: '/mode?category=school' },
    { label: 'Engineering', href: '/mode?category=college' },
  ],
  Company: [
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ],
  Boards: [
    { label: 'CBSE', href: '/mode?category=school' },
    { label: 'ICSE', href: '/mode?category=school' },
    { label: 'IGCSE', href: '/mode?category=school' },
    { label: 'IB', href: '/mode?category=school' },
    { label: 'Kerala_State', href: '/mode?category=school' },
    { label: 'Kerala_University', href: '/mode?category=college' },
  ],
  Legal: [
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Privacy Policy', href: '/privacy' },
  ],
};

const socials = [
  { icon: BsInstagram, href: 'https://www.instagram.com/zenqbedutech/', label: 'Instagram' },
  { icon: FaFacebookF, href: 'https://www.facebook.com/zenqbedutech', label: 'Facebook' },
  { icon: BsTwitterX, href: 'https://x.com/zenqbedutech', label: 'X' },
  { icon: FaDiscord, href: 'https://discord.com/channels/1478935453494149411/1478936654818312202', label: 'Discord' },
];

export default function Footer() {
  return (
    <footer className="relative border-t border-white/5">
      {/* CTA Banner */}
      <div className="section-padding !pb-12">
        <div className="container-custom mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-strong rounded-3xl p-10 md:p-16 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-accent-600/20" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-display text-white mb-4">
                Ready to <span className="gradient-text">level up</span>?
              </h2>
              <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
                Join thousands of students already using ZenQB to ace their exams.
              </p>
              <Link to="/signup" onClick={() => trackEvent("signup_started", { source: "landing_footer_cta" })} className="btn-primary text-lg !px-12 !py-4 inline-block">
                <span className="relative z-10">Start Free Today</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer Links */}
      <div className="container-custom mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-12">
          {/* Brand */}
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center">
                <BsRobot className="text-xl text-white" />
              </div>
              <span className="text-2xl font-bold">
                <span className="text-white">Zen</span>
                <span className="gradient-text">QB</span>
              </span>
            </Link>
            <p className="text-sm text-gray-500 leading-relaxed mb-4 max-w-xs">
              AI-powered exam preparation platform helping students practice smarter and score higher.
            </p>
            <div className="flex gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  onClick={() => trackEvent("social_link_clicked", { platform: s.label })}
                  className="w-9 h-9 glass rounded-xl flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all"
                >
                  <s.icon className="text-sm" />
                </a>
              ))}
            </div>
          </div>

          {/* Link Groups */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="font-semibold text-white text-sm mb-4">{title}</h4>
              <ul className="space-y-2.5">
                {links.map((link) => (
                  <li key={link.label}>
                    <Link to={link.href} onClick={() => trackEvent("footer_link_clicked", { label: link.label, section: title })} className="text-sm text-gray-500 hover:text-gray-300 transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-600">
            &copy; {new Date().getFullYear()} ZenQB. All rights reserved.
          </p>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <BsEnvelope className="text-gray-500" />
            <a href="mailto:hello@zenqb.com" className="hover:text-gray-400 transition-colors">
              hello@zenqb.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
